import React, { useState } from "react";
import type { VoidFunctionComponent } from "react";
import s from "./GenericModalDocumentation.module.scss";
import { Example } from "../Example";
import Button from "../../../components/common/Button";
import { GenericModal } from "../../../components/common/genericModal/GenericModal";
import { ComponentDescription } from "../documentationTools/ComponentDescription";
import { Property } from "../documentationTools/Property";
import { getLoremIpsumText } from "../../../util/string-util";
import { miscImages } from "../../../config/config";
import { PropertyListHeader } from "../documentationTools/PropertyListHeader";
import { IPropertyWrapper } from "../documentationTools/DocumentationTypes";
import { sortProperties } from "../documentationTools/documentation-utility";

export interface IGenericModalDocumentationProps {}

export const GenericModalDocumentation: VoidFunctionComponent<IGenericModalDocumentationProps> = ({}) => {
  const [canShowWhiteBackgroundModal, setCanShowWhiteBackgroundModal] = useState(false);
  const [canShowPurpleBackgroundModal, setCanShowPurpleBackgroundModal] = useState(false);
  const [canShowMaxWidthModal, setCanShowMaxWidthModal] = useState(false);
  const [canShowPaddingModal, setCanShowPaddingModal] = useState(false);
  const [canShowNoPaddingModal, setCanShowNoPaddingModal] = useState(false);
  const [canShowStringTitleModal, setCanShowTitleModal] = useState(false);
  const [canShowNavigationNoteModal, setCanShowNavigationNoteModal] = useState(false);
  const [canShowCloseButtonIsTitleModal, setCanCloseButtonInTitleModal] = useState(false);
  const [canShowCloseButtonInBodyModal, setCanShowCloseButtonInBodyModal] = useState(false);
  const [canShowChildrenModal, setCanShowChildrenModal] = useState(false);
  const [canShowReactNodeTitleModal, setCanShowReactNodeTitleModal] = useState(false);

  let propertyIndex = 0;

  const properties: IPropertyWrapper[] = [
    {
      isRequired: false,
      name: "backgroundColor",
      element: (
        <Property
          key={++propertyIndex}
          name={"backgroundColor"}
          description={
            'Determines the background color of the body of the modal. Can be "white" or "purple".'
          }
          isRequired={false}
          type={"string"}
          defaultValue={'"white"'}
          examples={
            <>
              <Example title={"Modal with white background"}>
                <Button
                  onClick={() => {
                    setCanShowWhiteBackgroundModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowWhiteBackgroundModal}
                  onClose={() => {
                    setCanShowWhiteBackgroundModal(false);
                  }}
                  backgroundColor={"white"}
                >
                  This modal has a white background.
                </GenericModal>
              </Example>
              <br />

              <Example title={"Modal with purple background"}>
                <Button
                  onClick={() => {
                    setCanShowPurpleBackgroundModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowPurpleBackgroundModal}
                  onClose={() => {
                    setCanShowPurpleBackgroundModal(false);
                  }}
                  backgroundColor={"purple"}
                >
                  This modal has a purple background.
                </GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: true,
      name: "canShow",
      element: (
        <Property
          key={++propertyIndex}
          name={"canShow"}
          description={"Determines whether or not the modal should be shown."}
          isRequired={true}
          type={"boolean | () => boolean"}
        />
      ),
    },
    {
      isRequired: false,
      name: "children",
      element: (
        <Property
          key={++propertyIndex}
          name={"children"}
          description={"Contents that should appear in the modal body"}
          isRequired={false}
          type={"ReactNode"}
          examples={
            <>
              <Example title={"Modal with children"}>
                <Button
                  onClick={() => {
                    setCanShowChildrenModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowChildrenModal}
                  onClose={() => {
                    setCanShowChildrenModal(false);
                  }}
                >
                  This is where the children go. Can be a simple string like
                  this or a ReactNode.
                </GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: false,
      name: "maxWidth",
      element: (
        <Property
          key={++propertyIndex}
          name={"maxWidth"}
          description={
            'Determines the maximum width of the modal. Can be any valid CSS width value (e.g. "450px", "80%", "30vw")'
          }
          isRequired={false}
          type={"string"}
          defaultValue={"max(30vw, 345px)"}
          examples={
            <>
              <Example title={"Modal with maxWidth of 400px"}>
                <Button
                  onClick={() => {
                    setCanShowMaxWidthModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowMaxWidthModal}
                  onClose={() => {
                    setCanShowMaxWidthModal(false);
                  }}
                  maxWidth={"400px"}
                >
                  {getLoremIpsumText()}
                </GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: false,
      name: "onClose",
      element: (
        <Property
          key={++propertyIndex}
          name={"onClose"}
          description={
            "Controls what happens when the close button is pressed. If provided also causes the close button to be displayed."
          }
          isRequired={false}
          type={"() => void"}
        />
      ),
    },
    {
      isRequired: false,
      name: "padBody",
      element: (
        <Property
          key={++propertyIndex}
          name={"padBody"}
          description={
            "Whether or not to include padding on the body of the modal."
          }
          isRequired={false}
          type={"boolean"}
          defaultValue={"true"}
          examples={
            <>
              <Example title={"Modal with padding"}>
                <Button
                  onClick={() => {
                    setCanShowPaddingModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowPaddingModal}
                  onClose={() => {
                    setCanShowPaddingModal(false);
                  }}
                  padBody={true}
                >
                  {getLoremIpsumText(500)}
                </GenericModal>
              </Example>

              <Example title={"Modal without padding"}>
                <Button
                  onClick={() => {
                    setCanShowNoPaddingModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowNoPaddingModal}
                  onClose={() => {
                    setCanShowNoPaddingModal(false);
                  }}
                  padBody={false}
                >
                  {getLoremIpsumText(500)}
                </GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: false,
      name: "showCloseButtonInBody",
      element: (
        <Property
          key={++propertyIndex}
          name={"showCloseButtonInBody"}
          description={
            "If true, a close button will be shown in the body of the modal instead of in the title bar."
          }
          isRequired={false}
          type={"boolean"}
          examples={
            <>
              <Example title={"Modal with close button in title"}>
                <Button
                  onClick={() => {
                    setCanCloseButtonInTitleModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowCloseButtonIsTitleModal}
                  onClose={() => {
                    setCanCloseButtonInTitleModal(false);
                  }}
                  showCloseButtonInBody={false}
                  title={"Modal with close button in title"}
                >
                  {getLoremIpsumText(100)}
                </GenericModal>
              </Example>

              <Example title={"Modal with close button in body"}>
                <Button
                  onClick={() => {
                    setCanShowCloseButtonInBodyModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowCloseButtonInBodyModal}
                  onClose={() => {
                    setCanShowCloseButtonInBodyModal(false);
                  }}
                  showCloseButtonInBody={true}
                  title={"Modal with close button in body"}
                >
                  <div style={{ width: "300px" }}>{getLoremIpsumText(100)}</div>
                </GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: false,
      name: "title",
      element: (
        <Property
          key={++propertyIndex}
          name={"title"}
          description={"The title of the modal."}
          isRequired={false}
          type={"string | ReactNode"}
          examples={
            <>
              <Example title={"Modal with a string title"}>
                <Button
                  onClick={() => {
                    setCanShowTitleModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowStringTitleModal}
                  onClose={() => {
                    setCanShowTitleModal(false);
                  }}
                  title={"My title"}
                ></GenericModal>
              </Example>

              <Example title={"Modal with a ReactNode title"}>
                <Button
                  onClick={() => {
                    setCanShowReactNodeTitleModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowReactNodeTitleModal}
                  onClose={() => {
                    setCanShowReactNodeTitleModal(false);
                  }}
                  title={
                    <>
                      Headphones!!
                      <img
                        src={miscImages.headphones}
                        alt=""
                        width={50}
                        height={40}
                      />
                    </>
                  }
                ></GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
    {
      isRequired: false,
      name: "navigationNote",
      element: (
        <Property
          key={++propertyIndex}
          name={"navigationNote"}
          description={<>
            A midi keyboard shortcut for the GenericModal close button.
            <span className={s.warning}> NOTE: Only works if a keyboard is plugged in. Piano note icon only appears if keyboard is plugged in.</span>
          </>}
          isRequired={false}
          type={'"C" | "D" | "B"'}
          examples={
            <>
              <Example title={'navigationNote = "B"'}>
                <Button
                  onClick={() => {
                    setCanShowNavigationNoteModal(true);
                  }}
                  text={"Show"}
                />
                <GenericModal
                  canShow={() => canShowNavigationNoteModal}
                  onClose={() => {
                    setCanShowNavigationNoteModal(false);
                  }}
                  title={"Modal with a navigationNote set"}
                  navigationNote={"B"}
                ></GenericModal>
              </Example>
            </>
          }
        />
      ),
    },
  ];

  sortProperties(properties);

  return (
    <div className={s.container}>
      <ComponentDescription
        name={"GenericModal"}
        description={
          <>
            <div>
              A modal is a dialog that appears on top of the main content.
            </div>
            <div>
              It blocks interaction with the main content until it is dismissed.
            </div>
            <div>
              Modals are used for important information or actions that require user attention.
            </div>
          </>
        }
      />

      <PropertyListHeader properties={properties} />

      {properties.map((property, index) => {
        return (
          <React.Fragment key={index}>
            {property.element}
            {index < properties.length - 1 && (
              <hr className={s.propertyDivider} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
